import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JobProfileContext } from 'Views/JobProfile/JobProfile.context';
import { deleteJobProfile, getSummaryJobProfile } from './services';
import {
	SummaryJobProfileType,
	SummaryJobRiskFactors,
	SymmaryJobRisks,
} from 'Views/JobProfile/pages/SummaryRisk/SummaryRisk.types';
import { formatterGetSummaryJobProfile } from './formatter/formatter';
import { summaryJobProfileEmptyState } from './constants/emptyState';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { useToast } from '@welbe/web-design-system';

export const useSummaryRisk = () => {
	const { toast } = useToast();
	const navigate = useNavigate();
	const {
		id,
		jobProfileType,
		setSummaryLoading,
		shouldUpdateSummary,
		setShouldUpdateSummary,
		setSummaryMountingLoading,
	} = useContext(JobProfileContext);
	const [isLoading, setIsLoading] = useState(false);
	const { getLocalStorage, setLocalStorage } = useLocalStorage();

	const [summaryJobProfile, setSummaryJobProfile] =
		useState<SummaryJobProfileType>(summaryJobProfileEmptyState);
	const [openModalFactor, setOpenModalFactor] = useState(false);
	const [openModalDelete, setOpenModalDelete] = useState(false);
	const [openModalDeleteConfirmation, setOpenModalDeleteConfirmation] =
		useState(false);
	const [openModalLinkEmployees, setOpenModalLinkEmployees] = useState(false);
	const [openModalLinkTutorial, setOpenModalLinkTutorial] = useState(false);
	const [openModalLinkCsv, setOpenModalLinkCsv] = useState(false);

	const [hasError, setHasError] = useState(false);
	const [selectedRisk, setSelectedRisk] = useState<SymmaryJobRisks | null>(
		null,
	);
	const [selectedFactor, setSelectedFactor] =
		useState<SummaryJobRiskFactors | null>(null);

	const preventReRender = useRef(false);

	const fetchSummaryJobProfile = async (silent?: boolean) => {
		try {
			!silent && setIsLoading(true);
			const response = await getSummaryJobProfile(id);
			setSummaryJobProfile(
				formatterGetSummaryJobProfile(response?.data?.content),
			);
			return true;
		} catch (error) {
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	};

	const handleRemountComponent = async () => {
		try {
			setSummaryMountingLoading(true);
			await fetchSummaryJobProfile(true);
		} catch (error) {
			console.error(error);
		} finally {
			setSummaryMountingLoading(false);
			setShouldUpdateSummary(false);
		}
	};

	useEffect(() => {
		if (!preventReRender.current) {
			fetchSummaryJobProfile();
			preventReRender.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (shouldUpdateSummary) {
			handleRemountComponent();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldUpdateSummary]);

	const handleFinish = () => {
		navigate(`/job-profile`);
	};

	const handleEditProfile = () => {
		navigate(`/job-profile/profile/${jobProfileType}/${id}`);
	};

	const handleEditRisk = (riskId: string) => {
		navigate(`/job-profile/risk-factors/${jobProfileType}/${id}/${riskId}`);
	};

	const handleOpenModalFactor = (
		risk: SymmaryJobRisks,
		factor: SummaryJobRiskFactors,
	) => {
		setSelectedRisk(risk);
		setSelectedFactor(factor);
		setOpenModalFactor(true);
	};

	const handleCloseModalFactor = () => {
		setSelectedRisk(null);
		setSelectedFactor(null);
		setOpenModalFactor(true);
	};

	const handleOpenModalDelete = () => {
		setOpenModalDelete(true);
	};

	const handleCloseModalDelete = () => {
		setOpenModalDelete(false);
	};

	const handleDelete = async () => {
		setIsLoading(true);
		try {
			await deleteJobProfile(id);
			setOpenModalDeleteConfirmation(true);
		} catch {
			toast.error('Error al eliminar el perfil de riesgo. Intente nuevamente.');
		} finally {
			handleCloseModalDelete();
			setIsLoading(false);
		}
	};

	const handleOpenModalLinkEmployees = () => {
		setOpenModalLinkEmployees(true);
	};

	const handleCloseModalLinkEmployees = () => {
		setOpenModalLinkEmployees(false);
	};

	const handleCloseModalDeleteConfirmation = () => {
		setOpenModalDeleteConfirmation(false);
		navigate(`/job-profile`);
	};

	const handleConfirmModalLinkEmployees = (linkEmployeeKind: string) => {
		if (linkEmployeeKind === 'manual') {
			navigate('/employees');
			return;
		}
		const showedTutorial = Boolean(getLocalStorage('linkEmployeeTutorial'));
		setOpenModalLinkEmployees(false);
		if (!showedTutorial) {
			setOpenModalLinkTutorial(true);
			return;
		}
		setOpenModalLinkCsv(true);
	};

	const handleCloseModalLinkTutorial = () => {
		setLocalStorage('linkEmployeeTutorial', 'true');
		setOpenModalLinkTutorial(false);
		setOpenModalLinkCsv(true);
	};

	const handleCloseModalLinkCsv = () => {
		setOpenModalLinkCsv(false);
	};

	const handleSuccessModalLinkCsv = () => {
		navigate(`/employees/${jobProfileType}/${id}`);
	};

	useEffect(() => {
		setSummaryLoading(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	return {
		summaryJobProfile,
		isLoading,
		hasError,
		selectedRisk,
		selectedFactor,
		openModalFactor,
		openModalDelete,
		openModalDeleteConfirmation,
		openModalLinkEmployees,
		openModalLinkTutorial,
		openModalLinkCsv,
		handleFinish,
		handleEditProfile,
		handleEditRisk,
		handleOpenModalFactor,
		handleCloseModalFactor,
		handleOpenModalDelete,
		handleCloseModalDelete,
		handleDelete,
		handleCloseModalDeleteConfirmation,
		handleOpenModalLinkEmployees,
		handleCloseModalLinkEmployees,
		handleConfirmModalLinkEmployees,
		handleCloseModalLinkTutorial,
		handleCloseModalLinkCsv,
		handleSuccessModalLinkCsv,
	};
};
